import React, { useEffect } from 'react'
import { Dialog } from '@mui/material'
import ModalTitleContainer from 'components/Modal/components/ModalTitleContainer'
import { StyledButton } from 'components/Buttons/DefaultButton/DefaultButton.styles'
import styled from 'styled-components'
import WarningIcon from '@mui/icons-material/Warning'

const StyledDialog = styled(Dialog)`
  && {
    z-index: 99999 !important;
    
    .MuiBackdrop-root {
      background-color: rgba(0, 0, 0, 0.7);
      backdrop-filter: blur(4px);
    }
    
    .MuiDialog-paper {
      border-radius: 8px;
      max-height: 90vh;
      overflow-y: auto;
      margin: 16px;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
    }
  }
`

const ModalContainer = styled.div`
  padding: 2rem 2rem 2.5rem;
  background: white;
  width: 100%;
  max-width: 28rem;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  @media (max-width: 600px) {
    padding: 1.5rem 1.25rem 2rem;
  }
`

const WarningIconContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 1.25rem;
  
  svg {
    color: #FB7672;
    font-size: 3.5rem;
  }
`

const Divider = styled.div`
  height: 2px;
  background: linear-gradient(to right, #FB7672, #FB7672);
  margin: 1.25rem 0 2rem;
  width: 100%;
`

const ModalDescription = styled.div`
  color: rgba(0, 0, 0, 0.87);
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
  text-align: center;
  margin-bottom: 1.75rem;
  width: 100%;
`

const LocationNameBox = styled.div`
  background: #FFF5F5;
  border: 1px solid #FFE0E0;
  border-radius: 8px;
  padding: 1.125rem 1.5rem;
  margin-bottom: 1.75rem;
  text-align: center;
  width: 100%;
  
  font-size: 1rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.87);
`

const WarningBox = styled.div`
  background: #FFF5F5;
  border: 1px solid #FFE0E0;
  border-radius: 8px;
  padding: 1.125rem;
  margin-bottom: 2rem;
  display: flex;
  align-items: flex-start;
  gap: 0.875rem;
  width: 100%;
  
  svg {
    color: #FB7672;
    font-size: 1.375rem;
    flex-shrink: 0;
    margin-top: 1px;
  }
`

const WarningText = styled.div`
  color: rgba(0, 0, 0, 0.87);
  font-size: 0.9375rem;
  line-height: 1.5;
  
  strong {
    font-weight: 600;
  }
`

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
  width: 100%;
`

const DeleteButton = styled(StyledButton)`
  && {
    background: #FB7672 !important;
    color: white !important;
    width: 100%;
    padding: 0.875rem 1.5rem;
    font-size: 1rem;
    font-weight: 600;
    text-transform: none;
    border-radius: 8px;
    letter-spacing: 0.3px;
    
    &:hover {
      background: #E86662 !important;
    }
    
    &:disabled {
      background: #FFB5B3 !important;
      color: white !important;
    }
  }
`

const CancelButton = styled(StyledButton)`
  && {
    background: white !important;
    color: rgba(0, 0, 0, 0.6) !important;
    border: none !important;
    width: 100%;
    padding: 0.875rem 1.5rem;
    font-size: 1rem;
    font-weight: 500;
    text-transform: none;
    box-shadow: none !important;
    letter-spacing: 0.2px;
    
    &:hover {
      background: #f5f5f5 !important;
      box-shadow: none !important;
    }
    
    &:disabled {
      opacity: 0.5;
    }
  }
`

interface DeleteLocationModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  locationName: string
  isDeleting: boolean
}

const DeleteLocationModal: React.FC<DeleteLocationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  locationName,
  isDeleting
}) => {
  // Lock body scroll when modal is open, similar to OfferModal
  useEffect(() => {
    const root = document.getElementById('root');
    const cls = 'no-vertical-scroll';
    if (isOpen) {
      document.body.classList.add(cls);
      document.documentElement.classList.add(cls);
      root && root.classList.add(cls);
    } else {
      document.body.classList.remove(cls);
      document.documentElement.classList.remove(cls);
      root && root.classList.remove(cls);
    }
    return () => {
      document.body.classList.remove(cls);
      document.documentElement.classList.remove(cls);
      root && root.classList.remove(cls);
    };
  }, [isOpen]);

  return (
    <StyledDialog
      open={isOpen}
      onClose={isDeleting ? undefined : onClose}
      PaperProps={{ 
        sx: { zIndex: 99999 }, 
        className: 'no-fastclick' 
      }}
      BackdropProps={{ 
        className: 'no-fastclick', 
        style: { touchAction: 'none' } 
      }}
      sx={{ zIndex: 99999 }}
      maxWidth="sm"
      fullWidth
      disableScrollLock={false}
    >
      <ModalContainer>
        <WarningIconContainer>
          <WarningIcon />
        </WarningIconContainer>
        
        <ModalTitleContainer 
          title="Delete Location?" 
          onClose={isDeleting ? undefined : onClose}
          center={true}
        />
        
        <Divider />
        
        <ModalDescription>
          You are about to permanently delete this location from your account:
        </ModalDescription>

        <LocationNameBox>
          {locationName}
        </LocationNameBox>

        <WarningBox>
          <WarningIcon />
          <WarningText>
            <strong>This action cannot be undone.</strong> All data associated with this location will be permanently removed.
          </WarningText>
        </WarningBox>

        <ButtonContainer>
          <DeleteButton
            variant="contained"
            size="large"
            onClick={onConfirm}
            disabled={isDeleting}
            className="no-fastclick"
          >
            {isDeleting ? 'Deleting...' : 'Yes, Delete Location'}
          </DeleteButton>
          <CancelButton
            variant="text"
            size="large"
            onClick={onClose}
            disabled={isDeleting}
            className="no-fastclick"
          >
            Cancel
          </CancelButton>
        </ButtonContainer>
      </ModalContainer>
    </StyledDialog>
  )
}

export default DeleteLocationModal

